import React, { useState, useEffect } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Trash, X, UserPlus, Upload, FileText } from 'lucide-react';
import { formatIndianCurrency } from '../utils/numberToWords';
import { getEscalationInfo } from '../utils/rentEscalation';
import { Select, Input, InputNumber } from 'antd';
import { Search } from 'lucide-react';
import { fetchInvoiceRules } from '../utils/api';

function Clients({ clients, onAddClient, onUpdateClient, onRemoveClient, onExport, onImport, config, facilities, currentUser }) {
  // Helpers to format date for display (DD/MM/YYYY) and to serialize (YYYY-MM-DD)
  const toDisplayDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const toISODate = (display) => {
    if (!display) return '';
    const m = String(display).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return display; // fallback to original if already ISO
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  };

  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    registeredName: '',
    billToAddress: '',
    shipToAddress: '',
    phone: '',
    gstin: '',
    bankAccountId: '',
    rentAmount: '',
    securityDeposit: '',
    escalationPercentage: '',
    rentStartDate: '',
    facilityId: ''
  });

  // Invoice rules for auto-assigning bank account
  const [invoiceRules, setInvoiceRules] = useState([]);

  // Load invoice rules on mount
  useEffect(() => {
    const loadInvoiceRules = async () => {
      try {
        const rules = await fetchInvoiceRules();
        setInvoiceRules(Array.isArray(rules) ? rules : []);
      } catch (error) {
        console.error('Failed to load invoice rules:', error);
        setInvoiceRules([]);
      }
    };
    loadInvoiceRules();
  }, []);

  // Auto-assign bank account when facility changes
  useEffect(() => {
    if (formData.facilityId && invoiceRules.length > 0) {
      // Find first rule matching the selected facility
      const matchingRule = invoiceRules.find(
        rule => String(rule.facilityId) === String(formData.facilityId)
      );
      if (matchingRule) {
        setFormData(prev => ({
          ...prev,
          bankAccountId: matchingRule.bankAccountId
        }));
      } else {
        // Clear bank account if no rule found
        setFormData(prev => ({
          ...prev,
          bankAccountId: ''
        }));
      }
    } else if (!formData.facilityId) {
      // Clear bank account if facility is cleared
      setFormData(prev => ({
        ...prev,
        bankAccountId: ''
      }));
    }
  }, [formData.facilityId, invoiceRules]);

  // Document upload fields
  const DOC_FIELDS = [
    { key: 'aadhar', label: 'Aadhar' },
    { key: 'pan', label: 'PAN' },
    { key: 'companyIncorporationCertificate', label: 'Company Incorporation Certificate' },
    { key: 'gstCertificate', label: 'GST Certificate' },
    { key: 'gstrFiling', label: 'GSTR Filing' },
    { key: 'leaseDeed', label: 'Lease Deed' },
    { key: 'loi', label: 'LOI' },
    { key: 'termsheet', label: 'Term Sheet' },
    { key: 'serviceAgreement', label: 'Service Agreement' },
  ];
  const [docFiles, setDocFiles] = useState({});

  const setDocFile = (key, file) => setDocFiles(prev => ({ ...prev, [key]: file }));
  const clearDocFile = (key) => setDocFiles(prev => ({ ...prev, [key]: undefined }));

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024)) || 0;
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingClient) {
      // Update existing client (support documents)
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === 'rentStartDate') {
          fd.append(k, toISODate(v));
        } else {
          fd.append(k, v);
        }
      });
      DOC_FIELDS.forEach(({ key }) => { const f = docFiles[key]; if (f) fd.append(key, f); });
      try {
        await onUpdateClient(editingClient._id || editingClient.id, fd);
        setEditingClient(null);
        setDocFiles({});
        // Reset form data
        setFormData({ name: '', registeredName: '', billToAddress: '', shipToAddress: '', phone: '', gstin: '', bankAccountId: '', rentAmount: '', securityDeposit: '', escalationPercentage: '', rentStartDate: '', facilityId: '' });
        setShowForm(false);
      } catch (err) {
        alert('Failed to update client: ' + (err?.message || ''));
      }
      return;
    }

    // Build multipart form data to include documents
    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (k === 'rentStartDate') {
        fd.append(k, toISODate(v));
      } else {
        fd.append(k, v);
      }
    });
    DOC_FIELDS.forEach(({ key }) => {
      const f = docFiles[key];
      if (f) fd.append(key, f);
    });

    try {
      await onAddClient(fd);
      // Reset form data
      setFormData({ name: '', registeredName: '', billToAddress: '', shipToAddress: '', phone: '', gstin: '', bankAccountId: '', rentAmount: '', securityDeposit: '', escalationPercentage: '', rentStartDate: '', facilityId: '' });
      setDocFiles({});
      setShowForm(false);
    } catch (err) {
      alert('Failed to add client: ' + (err?.message || ''));
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      registeredName: client.registeredName || '',
      billToAddress: client.billToAddress,
      shipToAddress: client.shipToAddress,
      phone: client.phone,
      gstin: client.gstin,
      bankAccountId: client.bankAccountId || '',
      rentAmount: client.rentAmount || '',
      securityDeposit: client.securityDeposit || '',
      escalationPercentage: client.escalationPercentage || '',
      rentStartDate: toDisplayDate(client.rentStartDate),
      facilityId: client.facilityId || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingClient(null);
    // Reset form data
    setFormData({ name: '', registeredName: '', billToAddress: '', shipToAddress: '', phone: '', gstin: '', bankAccountId: '', rentAmount: '', securityDeposit: '', escalationPercentage: '', rentStartDate: '', facilityId: '' });
  };

  const handleAddNewClient = () => {
    setEditingClient(null);
    // Reset form data
    setFormData({ name: '', registeredName: '', billToAddress: '', shipToAddress: '', phone: '', gstin: '', bankAccountId: '', rentAmount: '', securityDeposit: '', escalationPercentage: '', rentStartDate: '', facilityId: '' });
    setShowForm(true);
  };

  // Search and facility filter
  const [searchTerm, setSearchTerm] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');

  const filteredClients = clients.filter(client => {
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch = !search ||
      (client.name && client.name.toLowerCase().includes(search)) ||
      (client.registeredName && client.registeredName.toLowerCase().includes(search)) ||
      (client.gstin && client.gstin.toLowerCase().includes(search));
    const matchesFacility = !facilityFilter || String(client.facilityId) === String(facilityFilter);
    return matchesSearch && matchesFacility;
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>{currentUser?.role === 'admin' ? 'Manage Clients' : 'View Clients'}</h1>
          <p>Manage client information and billing details</p>
        </div>


        <div className="page-actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="search-input" style={{ minWidth: 240 }}>
            <Input
              prefix={<Search size="1.2em" />}
              placeholder="Search by name, registered name, or GSTIN..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              allowClear
            />
          </div>
          <div className="search-input" style={{ minWidth: 200 }}>
            <Select
              style={{ width: '100%' }}
              value={facilityFilter || undefined}
              onChange={(v) => setFacilityFilter(v || '')}
              placeholder="All Facilities"
              options={(facilities || []).map(f => ({ label: f.name, value: f.id }))}
              allowClear
              showSearch
              optionFilterProp="label"
              size="large"
            />
          </div>
          {!showForm && currentUser?.role === 'admin' && (
            <button
              className="btn btn-primary"
              onClick={handleAddNewClient}
              style={{
                borderRadius: 10,
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <UserPlus size="1em" />
              Add New Client
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="modal-right-overlay">
          <div className="modal-right-content modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {editingClient ? 'Edit Client' : 'Add New Client'}
                </h2>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }}
                  onClick={() => setShowForm(false)}
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form id="client-form" onSubmit={handleSubmit} style={{ marginBottom: '0' }} encType="multipart/form-data">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0px' }}>
                  <div className="form-group">
                    <label>Client Name</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Registered Name</label>
                    <Input
                      value={formData.registeredName}
                      onChange={(e) => setFormData({ ...formData, registeredName: e.target.value })}
                      placeholder="Enter registered company name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bill To Address</label>
                    <Input.TextArea
                      value={formData.billToAddress}
                      onChange={(e) => setFormData({ ...formData, billToAddress: e.target.value })}
                      rows={3}
                      required
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Ship To Address</label>
                    <Input.TextArea
                      value={formData.shipToAddress}
                      onChange={(e) => setFormData({ ...formData, shipToAddress: e.target.value })}
                      rows={3}
                      placeholder="Leave blank if same as bill to address"
                      style={{ resize: 'vertical', minHeight: '80px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>GSTIN</label>
                    <Input
                      value={formData.gstin}
                      onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Facility</label>
                    <Select
                      style={{ width: '100%' }}
                      value={formData.facilityId || undefined}
                      onChange={(v) => setFormData({ ...formData, facilityId: v })}
                      placeholder="Select a facility"
                      options={(facilities || []).map(fac => {
                        const facilityName = fac.name || fac.facilityName || `Facility ${fac.id}`;
                        return { label: facilityName, value: fac.id };
                      })}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      size="large"
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Rent Amount</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={formData.rentAmount === '' ? null : Number(formData.rentAmount)}
                      onChange={(val) => setFormData({ ...formData, rentAmount: val ?? '' })}
                      placeholder="Enter monthly rent amount"
                      step={0.01}
                      min={0}
                    />
                  </div>
                  <div className="form-group">
                    <label>Escalation Percentage</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={formData.escalationPercentage === '' ? null : Number(formData.escalationPercentage)}
                      onChange={(val) => setFormData({ ...formData, escalationPercentage: val ?? '' })}
                      placeholder="Enter escalation %"
                      step={0.01}
                      min={0}
                      max={100}
                    />
                  </div>
                  <div className="form-group">
                    <label>Rent Start Date</label>
                    <DatePicker
                      style={{ width: '100%' }}
                      value={formData.rentStartDate ? dayjs(toISODate(formData.rentStartDate)) : null}
                      onChange={(d) => setFormData({ ...formData, rentStartDate: d ? d.format('DD/MM/YYYY') : '' })}
                      format="DD/MM/YYYY"
                      placeholder="dd/mm/yyyy"
                      allowClear
                    />
                  </div>
                  <div className="form-group">
                    <label>Security Deposit</label>
                    <InputNumber
                      style={{ width: '100%' }}
                      value={formData.securityDeposit === '' ? null : Number(formData.securityDeposit)}
                      onChange={(val) => setFormData({ ...formData, securityDeposit: val ?? '' })}
                      placeholder="Enter security deposit amount"
                      step={0.01}
                      min={0}
                    />
                  </div>
                </div>
                <div className="config-section card-style" style={{ marginTop: '12px' }}>
                  <div className="config-section-header">
                    <h3>Client Documents</h3>
                  </div>
                  <div className="upload-grid">
                    {DOC_FIELDS.map(({ key, label }) => (
                      <div className="upload-item" key={key}>
                        <div className="upload-title">{label}</div>
                        {!docFiles[key] ? (
                          <label htmlFor={`file-${key}`} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: '2px dashed #e1e5ee',
                            borderRadius: 10, cursor: 'pointer', color: '#555', background: '#fafafa', width: '100%'
                          }}>
                            <Upload size={18} />
                            <span>Drop items here or <strong>Browse files</strong></span>
                            <input
                              id={`file-${key}`}
                              type="file"
                              accept="image/*,application/pdf"
                              style={{ display: 'none' }}
                              onChange={e => setDocFile(key, e.target.files?.[0])}
                            />
                          </label>
                        ) : (
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 14px', border: '1px solid #e6ecf1', borderRadius: 10, background: '#fff', width: '100%'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ width: 36, height: 36, background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FileText size={18} />
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: '#111827' }}>{docFiles[key]?.name}</div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>{formatBytes(docFiles[key]?.size)}</div>
                              </div>
                            </div>
                            <button type="button" className="btn btn-danger" onClick={() => clearDocFile(key)} title="Remove">
                              <Trash size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" form="client-form" className="btn btn-primary">
                  {editingClient ? 'Update Client' : 'Add Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-wrapper" style={{ marginTop: '20px' }}>
        <table className="table client-list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>GSTIN</th>
              <th>Facility</th>
              <th>Base Rent</th>
              <th>Current Rent</th>
              <th>Security Deposit</th>
              <th>Documents</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map(client => {
              const facility = facilities && facilities.find(fac => String(fac.id) === String(client.facilityId));
              const facilityName = facility ? (facility.name || facility.facilityName || `Facility ${facility.id}`) : 'Not selected';
              const docs = client.documents;
              const docCount = docs
                ? (docs instanceof Map
                  ? Array.from(docs.values()).filter(Boolean).length
                  : Object.values(docs).filter(Boolean).length)
                : 0;
              return (
                <tr key={client._id || client.id}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => navigate(`/client/${client._id || client.id}`)}
                      title="View Client Details"
                      style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <strong>{client.name}</strong>
                    </button>
                  </td>
                  <td>{client.gstin}</td>
                  <td>{facilityName}</td>
                  <td>{client.rentAmount ? formatIndianCurrency(Number(client.rentAmount), 0) : ''}</td>
                  <td>
                    {client.rentAmount && client.escalationPercentage && client.rentStartDate ? (() => {
                      const escalationInfo = getEscalationInfo(
                        client.rentAmount,
                        client.escalationPercentage,
                        client.rentStartDate,
                        new Date()
                      );
                      if (escalationInfo.hasEscalation) {
                        return (
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#2563eb' }}>
                              {formatIndianCurrency(escalationInfo.escalatedAmount, 0)}
                            </div>
                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                              +{escalationInfo.escalationPercentage}% ({escalationInfo.yearsElapsed} year{escalationInfo.yearsElapsed > 1 ? 's' : ''})
                            </div>
                          </div>
                        );
                      }
                      return formatIndianCurrency(Number(client.rentAmount), 0);
                    })() : (client.rentAmount ? formatIndianCurrency(Number(client.rentAmount), 0) : '')}
                  </td>
                  <td>{client.securityDeposit ? formatIndianCurrency(Number(client.securityDeposit), 0) : ''}</td>
                  <td>{docCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Clients; 